/**
 * Detail cards shown inline in the workbench conversation after each agent
 * tool call. Each card is a read-only condensed view of the full artifact
 * already stored in session.artifacts, styled to match the existing
 * onboarding wizard components (visual parity, no duplicated business logic).
 *
 *   ProfileDetailCard       — reads WorkbenchSession.artifacts.profile
 *   DictionaryDetailCard    — reads WorkbenchSession.artifacts.dictionary
 *   RulesDetailCard         — reads WorkbenchSession.artifacts.rules
 *   MappingDetailCard       — lazily fetches /api/mapping/runs/<run_id>
 *                             and delegates to MappingResults
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Table2,
  Hash,
  Type,
  Key,
  BookOpen,
  Info,
  Loader2,
  ExternalLink,
  Link2,
} from 'lucide-react';
import { mappingApi } from '../../../services/api';
import MappingResults from '../../mapping/MappingResults';

/* ═══════════════════════════════════════════════════════════════════════════
 *  PROFILE CARD
 * ═══════════════════════════════════════════════════════════════════════════ */

interface ProfileColumn {
  column_name: string;
  data_type: string;
  profiling: {
    null_percentage: number;
    unique_percentage: number;
    min?: number | null;
    max?: number | null;
    mean?: number | null;
    min_length?: number | null;
    max_length?: number | null;
    avg_length?: number | null;
    sample_values?: (string | number | null)[];
  };
}

interface ProfileTable {
  table_name: string;
  row_count: number;
  sampled_rows?: number;
  columns: ProfileColumn[];
}

interface ProfileResultLike {
  tables: ProfileTable[];
}

export const ProfileDetailCard: React.FC<{ profile: ProfileResultLike | null }> = ({ profile }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedTable, setExpandedTable] = useState<string | null>(null);

  if (!profile || !profile.tables || profile.tables.length === 0) return null;

  const totalRows = profile.tables.reduce((s, t) => s + (t.row_count || 0), 0);
  const totalCols = profile.tables.reduce((s, t) => s + (t.columns?.length || 0), 0);

  return (
    <CardShell
      icon={<Table2 className="w-3.5 h-3.5 text-blue-400" />}
      title="Profile details"
      subtitle={`${profile.tables.length} table(s) · ${totalRows.toLocaleString()} rows · ${totalCols} columns`}
      expanded={expanded}
      onToggle={() => setExpanded((e) => !e)}
    >
      <div className="space-y-3 p-3">
        {profile.tables.map((t) => (
          <div
            key={t.table_name}
            className="bg-cargo-surface border border-cargo-border rounded-lg overflow-hidden"
          >
            <button
              onClick={() => setExpandedTable(expandedTable === t.table_name ? null : t.table_name)}
              className="w-full flex items-center justify-between px-4 py-2 hover:bg-cargo-hover/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Table2 className="w-3.5 h-3.5 text-cargo-accent" />
                <span className="text-sm font-medium text-cargo-text">{t.table_name}</span>
                <span className="text-[11px] text-cargo-muted">
                  {t.row_count?.toLocaleString() ?? 0} rows · {t.columns?.length ?? 0} cols
                </span>
              </div>
              {expandedTable === t.table_name ? (
                <ChevronDown className="w-4 h-4 text-cargo-muted" />
              ) : (
                <ChevronRight className="w-4 h-4 text-cargo-muted" />
              )}
            </button>

            {expandedTable === t.table_name && (
              <div className="border-t border-cargo-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-cargo-panel text-cargo-muted text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-1.5">Column</th>
                      <th className="text-left px-3 py-1.5">Type</th>
                      <th className="text-right px-3 py-1.5">Null %</th>
                      <th className="text-right px-3 py-1.5">Unique %</th>
                      <th className="text-left px-3 py-1.5">Stats</th>
                      <th className="text-left px-3 py-1.5">Sample</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.columns.map((c) => {
                      const p = c.profiling || {};
                      const isNumeric = p.min !== null && p.min !== undefined;
                      return (
                        <tr
                          key={c.column_name}
                          className="border-t border-cargo-border hover:bg-cargo-hover/20"
                        >
                          <td className="px-3 py-1.5 font-medium text-cargo-text flex items-center gap-1.5">
                            {isNumeric ? (
                              <Hash className="w-3 h-3 text-blue-400" />
                            ) : (
                              <Type className="w-3 h-3 text-green-400" />
                            )}
                            {c.column_name}
                          </td>
                          <td className="px-3 py-1.5 text-cargo-muted">{c.data_type}</td>
                          <td className="px-3 py-1.5 text-right">
                            <NullBadge pct={p.null_percentage ?? 0} />
                          </td>
                          <td className="px-3 py-1.5 text-right text-cargo-text">
                            {(p.unique_percentage ?? 0).toFixed(1)}%
                          </td>
                          <td className="px-3 py-1.5 text-cargo-muted">
                            {isNumeric ? (
                              <span>
                                min={p.min} max={p.max}
                                {typeof p.mean === 'number' && <> · μ={p.mean.toFixed(1)}</>}
                              </span>
                            ) : p.min_length != null ? (
                              <span>
                                len {p.min_length}–{p.max_length}
                                {typeof p.avg_length === 'number' && <> · avg {p.avg_length.toFixed(1)}</>}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-cargo-muted max-w-[180px] truncate">
                            {(p.sample_values || []).slice(0, 3).join(', ') || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </CardShell>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  DICTIONARY CARD
 * ═══════════════════════════════════════════════════════════════════════════ */

interface DictColumn {
  name: string;
  data_type: string;
  primary_key?: boolean;
  foreign_key?: boolean;
  description?: string;
}

interface DictTable {
  table_name: string;
  columns: DictColumn[];
}

interface DictionaryResultLike {
  tables: DictTable[];
}

export const DictionaryDetailCard: React.FC<{ dictionary: DictionaryResultLike | null }> = ({ dictionary }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedTable, setExpandedTable] = useState<string | null>(null);

  if (!dictionary || !dictionary.tables || dictionary.tables.length === 0) return null;

  const describedCols = dictionary.tables.reduce(
    (s, t) => s + (t.columns || []).filter((c) => c.description).length,
    0,
  );
  const totalCols = dictionary.tables.reduce((s, t) => s + (t.columns?.length || 0), 0);

  return (
    <CardShell
      icon={<BookOpen className="w-3.5 h-3.5 text-cargo-accent" />}
      title="Dictionary details"
      subtitle={`${dictionary.tables.length} table(s) · ${describedCols}/${totalCols} columns described`}
      expanded={expanded}
      onToggle={() => setExpanded((e) => !e)}
    >
      <div className="space-y-3 p-3">
        {dictionary.tables.map((t) => (
          <div
            key={t.table_name}
            className="bg-cargo-surface border border-cargo-border rounded-lg overflow-hidden"
          >
            <button
              onClick={() => setExpandedTable(expandedTable === t.table_name ? null : t.table_name)}
              className="w-full flex items-center justify-between px-4 py-2 hover:bg-cargo-hover/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Table2 className="w-3.5 h-3.5 text-cargo-accent" />
                <span className="text-sm font-medium text-cargo-text">{t.table_name}</span>
                <span className="text-[11px] text-cargo-muted">
                  {t.columns?.length ?? 0} cols
                </span>
              </div>
              {expandedTable === t.table_name ? (
                <ChevronDown className="w-4 h-4 text-cargo-muted" />
              ) : (
                <ChevronRight className="w-4 h-4 text-cargo-muted" />
              )}
            </button>

            {expandedTable === t.table_name && (
              <div className="border-t border-cargo-border">
                <table className="w-full text-xs">
                  <thead className="bg-cargo-panel text-cargo-muted text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-1.5 w-1/5">Column</th>
                      <th className="text-left px-3 py-1.5 w-1/6">Type</th>
                      <th className="text-center px-3 py-1.5 w-10">PK</th>
                      <th className="text-center px-3 py-1.5 w-10">FK</th>
                      <th className="text-left px-3 py-1.5">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.columns.map((c) => (
                      <tr
                        key={c.name}
                        className="border-t border-cargo-border hover:bg-cargo-hover/20"
                      >
                        <td className="px-3 py-1.5 font-medium text-cargo-text">{c.name}</td>
                        <td className="px-3 py-1.5 text-cargo-muted">{c.data_type}</td>
                        <td className="px-3 py-1.5 text-center">
                          {c.primary_key && (
                            <Key className="w-3 h-3 text-cargo-warning mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          {c.foreign_key && (
                            <Link2 className="w-3 h-3 text-blue-400 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-cargo-muted">
                          {c.description || <span className="text-cargo-muted/50">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </CardShell>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  RULES CARD
 * ═══════════════════════════════════════════════════════════════════════════ */

interface RuleLike {
  rule_name?: string;
  display_name?: string;
  column_name?: string;
  table_name?: string;
  category?: string;
  confidence?: 'low' | 'medium' | 'high';
  reason?: string;
  kwargs?: Record<string, unknown>;
  source?: string;
}

const CONF_BADGE: Record<string, { color: string; icon: string }> = {
  high: { color: 'bg-cargo-success/15 text-cargo-success', icon: '●' },
  medium: { color: 'bg-cargo-warning/15 text-cargo-warning', icon: '◐' },
  low: { color: 'bg-cargo-muted/15 text-cargo-muted', icon: '○' },
};

export const RulesDetailCard: React.FC<{ rules: RuleLike[] | null }> = ({ rules }) => {
  const [expanded, setExpanded] = useState(false);

  if (!rules || rules.length === 0) return null;

  const grouped: Record<string, RuleLike[]> = {};
  for (const r of rules) {
    const key = (r.category || 'uncategorized').toLowerCase();
    (grouped[key] = grouped[key] || []).push(r);
  }

  const high = rules.filter((r) => r.confidence === 'high').length;
  const med = rules.filter((r) => r.confidence === 'medium').length;
  const low = rules.filter((r) => r.confidence === 'low').length;

  return (
    <CardShell
      icon={<BookOpen className="w-3.5 h-3.5 text-cargo-accent" />}
      title="Suggested rules"
      subtitle={`${rules.length} rules · ${high} high · ${med} medium · ${low} low confidence`}
      expanded={expanded}
      onToggle={() => setExpanded((e) => !e)}
    >
      <div className="space-y-3 p-3 max-h-[28rem] overflow-y-auto">
        {Object.entries(grouped).map(([cat, items]) => (
          <div
            key={cat}
            className="bg-cargo-surface border border-cargo-border rounded-lg p-3"
          >
            <h4 className="text-xs font-semibold text-cargo-accent mb-2 flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" />
              <span className="capitalize">{cat}</span>
              <span className="text-cargo-muted font-normal">({items.length})</span>
            </h4>
            <div className="space-y-1.5">
              {items.map((r, i) => {
                const conf = r.confidence || 'low';
                const badge = CONF_BADGE[conf] || CONF_BADGE.low;
                return (
                  <div
                    key={i}
                    className="bg-cargo-panel rounded px-3 py-1.5 text-xs"
                  >
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${badge.color}`}>
                        {badge.icon} {conf}
                      </span>
                      {r.source === 'domain' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400">
                          domain
                        </span>
                      )}
                      <span className="text-cargo-text font-medium">
                        {r.display_name || r.rule_name || 'rule'}
                      </span>
                      {r.column_name && (
                        <span className="text-cargo-muted font-mono">
                          · {r.column_name}
                        </span>
                      )}
                      {r.table_name && (
                        <span className="text-cargo-muted text-[10px]">
                          on {r.table_name}
                        </span>
                      )}
                    </div>
                    {r.rule_name && r.display_name && (
                      <p className="text-[10px] text-cargo-muted font-mono">
                        {r.rule_name}
                      </p>
                    )}
                    {r.reason && (
                      <p className="text-[10px] text-cargo-muted/70 mt-0.5 flex items-start gap-1">
                        <Info className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                        {r.reason}
                      </p>
                    )}
                    {r.kwargs && Object.keys(r.kwargs).length > 0 && (
                      <p className="text-[10px] text-cargo-muted font-mono mt-0.5 truncate">
                        {Object.entries(r.kwargs).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(' · ')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </CardShell>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  MAPPING CARD — delegates to existing MappingResults
 * ═══════════════════════════════════════════════════════════════════════════ */

export const MappingDetailCard: React.FC<{ runId: string }> = ({ runId }) => {
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['mapping-run-detail', runId],
    queryFn: async () => (await mappingApi.getRun(runId)).data.payload,
    enabled: !!runId && expanded, // lazy — only fetch when expanded
  });

  return (
    <CardShell
      icon={<Link2 className="w-3.5 h-3.5 text-cargo-accent" />}
      title="Schema mapping details"
      subtitle={runId}
      expanded={expanded}
      onToggle={() => setExpanded((e) => !e)}
      rightSlot={
        <a
          href={`/mapping/runs/${runId}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-[11px] text-cargo-accent hover:underline"
        >
          Open full view <ExternalLink className="w-3 h-3" />
        </a>
      }
    >
      <div className="p-3 max-h-[32rem] overflow-y-auto">
        {isLoading && (
          <div className="flex items-center gap-2 text-cargo-muted text-xs p-4">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading mapping run…
          </div>
        )}
        {error && (
          <div className="text-cargo-error text-xs p-3">
            Failed to load mapping details. Open the full view instead.
          </div>
        )}
        {data?.mapping_result && (
          <MappingResults
            result={data.mapping_result}
            targetDictionary={data.target_dictionary_resolved || null}
          />
        )}
      </div>
    </CardShell>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  Shared card shell + helpers
 * ═══════════════════════════════════════════════════════════════════════════ */

function CardShell({
  icon,
  title,
  subtitle,
  expanded,
  onToggle,
  rightSlot,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  expanded: boolean;
  onToggle: () => void;
  rightSlot?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-cargo-panel/60 border border-cargo-border/60 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-cargo-hover/20 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-cargo-muted flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-cargo-muted flex-shrink-0" />
          )}
          {icon}
          <span className="text-xs font-semibold text-cargo-text">{title}</span>
          {subtitle && (
            <span className="text-[11px] text-cargo-muted truncate">{subtitle}</span>
          )}
        </div>
        {rightSlot && <div className="ml-3 flex-shrink-0">{rightSlot}</div>}
      </button>
      {expanded && children}
    </div>
  );
}

function NullBadge({ pct }: { pct: number }) {
  const color =
    pct === 0
      ? 'text-cargo-success'
      : pct < 5
      ? 'text-cargo-warning'
      : 'text-cargo-error';
  return <span className={`font-medium ${color}`}>{pct.toFixed(1)}%</span>;
}
